/**
 * @module deploy/heroku
 * @description SDK-free Heroku deployment slice contract.
 */

import type { DeployAdapter, DeploymentTarget } from "@geenius/adapters";

import {
  createProviderDeployAdapter,
  isProviderDeploymentTarget,
  type ProviderDeployAdapter,
  validateProviderDeploymentTarget,
} from "./provider-slice";

/**
 * Deployment target accepted by the Heroku provider slice.
 */
export type HerokuDeploymentTarget = DeploymentTarget & {
  readonly provider: "heroku";
};

/**
 * Heroku-scoped deployment adapter. Downstream wiring may accept a neutral
 * adapter, but the wrapped public slice only accepts Heroku targets.
 */
export type HerokuDeployAdapter = ProviderDeployAdapter<"heroku">;

/**
 * SDK-free manifest describing Heroku deployment capabilities and required
 * environment variables.
 */
export interface HerokuDeployProviderManifest {
  readonly provider: "heroku";
  readonly displayName: string;
  readonly runtime: "sdk-free-contract";
  readonly capabilities: readonly string[];
  readonly environmentVariables: readonly string[];
}

/**
 * Heroku deploy contract bundle with its manifest, wrapped adapter, and target
 * validator.
 */
export interface HerokuDeployContract {
  readonly provider: "heroku";
  readonly manifest: HerokuDeployProviderManifest;
  readonly adapter: HerokuDeployAdapter;
  validateTarget(target: unknown): HerokuDeploymentTarget;
}

/**
 * Provider manifest for SDK-free Heroku deployment adapters.
 */
export const HEROKU_DEPLOY_PROVIDER: HerokuDeployProviderManifest = {
  provider: "heroku",
  displayName: "Heroku",
  runtime: "sdk-free-contract",
  capabilities: [
    "project-deployments",
    "rollback",
    "deployment-lookup",
    "deployment-list",
    "health-check",
  ],
  environmentVariables: ["HEROKU_APP_ID", "HEROKU_API_KEY"],
};

/**
 * Checks whether a deployment target belongs to the Heroku provider.
 */
export function isHerokuDeploymentTarget(
  target: unknown,
): target is HerokuDeploymentTarget {
  return isProviderDeploymentTarget("heroku", target);
}

/**
 * Validates and narrows a deployment target for the Heroku provider slice.
 */
export function validateHerokuDeploymentTarget(
  target: unknown,
): HerokuDeploymentTarget {
  return validateProviderDeploymentTarget("heroku", target);
}

/**
 * Wraps a generic deployment adapter with Heroku target and result guards.
 */
export function createHerokuDeployAdapter(
  adapter: DeployAdapter,
): HerokuDeployAdapter {
  return createProviderDeployAdapter({ provider: "heroku", adapter });
}

/**
 * Creates a Heroku deploy contract bundle from a provider-neutral adapter.
 */
export function createHerokuDeployContract(
  adapter: DeployAdapter,
): HerokuDeployContract {
  return {
    provider: "heroku",
    manifest: HEROKU_DEPLOY_PROVIDER,
    adapter: createHerokuDeployAdapter(adapter),
    validateTarget: validateHerokuDeploymentTarget,
  };
}
